export class DynamicPricingCore {
    constructor(config) {

        if (!config) {
            throw new Error('Dynamic Pricing config is required');
        }

        this.config = config;
        this.cacheKey = config.cacheKey || 'dynamicPricingCache';
        this.batchSize = config.batch_size || 25;
        this.maxRetries = config.max_retries || 2;
        this.priceCache = this.loadCacheFromSession();
    }

    loadCacheFromSession() {
        try {
            const cached = sessionStorage.getItem(this.cacheKey);
            if (cached) {
                const data = JSON.parse(cached);
                if (Date.now() - data.timestamp < this.config.cache_duration) {
                    return data.prices;
                }
            }
        } catch (error) {
            return {};
        }
        return {};
    }

    updateCache(newPrices) {
        this.priceCache = { ...this.priceCache, ...newPrices };

        try {
            sessionStorage.setItem(this.cacheKey, JSON.stringify({
                timestamp: Date.now(),
                prices: this.priceCache
            }));
        } catch (error) {
            // Silently fail on storage errors
        }
    }

    getPricesFromCache(skus) {
        const cached = {};
        skus.forEach(sku => {
            if (this.priceCache[sku]) {
                cached[sku] = this.priceCache[sku];
            }
        });

        return cached;
    }

    async fetchPrices(skus) { // PARALLEL
        if (skus.length === 0) {
            return {};
        }

        const allPrices = {};

        // Split SKUs into batches using configurable batch size
        const batches = [];
        for (let i = 0; i < skus.length; i += this.batchSize) {
            batches.push(skus.slice(i, i + this.batchSize));
        }

        // console.log('DP: Fetching', skus.length, 'SKUs in', batches.length, 'batches (parallel, batch size:', this.batchSize + ')');

        // Process all batches in parallel
        const batchPromises = batches.map((batchSkus, index) =>
            this.fetchPricesBatchWithRetry(batchSkus, index).catch(error => {
                console.error('DP: Batch', index + 1, 'failed after all retries:', error);
                const pricingErrorMessage = 'Please refresh page for price';
                document.querySelectorAll('.f-price-item').forEach(el => {
                    el.innerHTML = `<span class="dp-error-message">${pricingErrorMessage}</span>`;
                });
                return {}; // Return empty object on failure
            })
        );

        const batchResults = await Promise.all(batchPromises);

        // Merge all batch results
        batchResults.forEach(batchPrices => {
            Object.assign(allPrices, batchPrices);
        });

        return allPrices;
    }

    async fetchPricesBatchWithRetry(skus, batchIndex = 0) {
        let lastError = null;
        const maxAttempts = this.maxRetries + 1; // Initial attempt + retries

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const result = await this.fetchPricesBatch(skus);

                if (result !== null) {
                    if (attempt > 1) {
                        // console.log('DP: Batch', batchIndex + 1, 'succeeded on attempt', attempt);
                    }
                    return result;
                }
            } catch (error) {
                lastError = error;
                console.warn('DP: Batch', batchIndex + 1, 'attempt', attempt, 'of', maxAttempts, 'failed:', error.message);

                if (attempt < maxAttempts) {
                    // Wait before retrying
                    const delay = 250 * attempt;
                    // console.log('DP: Retrying batch', batchIndex + 1, 'in', delay + 'ms...');
                    await this.sleep(delay);
                }
            }
        }

        // All attempts failed
        console.error('DP: Batch', batchIndex + 1, 'failed after', maxAttempts, 'attempts');
        throw lastError || new Error('All retry attempts failed');
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async fetchPricesBatch(skus) {
        if (skus.length === 0) {
            return {};
        }

        const requestBody = [{
            shipto_id: this.config.shipto_id,
            products: skus.map(sku => parseInt(sku))
        }];

        // console.log('DP: Fetching batch of', skus.length, 'SKUs');

        const response = await fetch(this.config.api.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'pipe_id': this.config.api.pipe_id
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Product Pricing API Error:', {
                status: response.status,
                statusText: response.statusText,
                url: this.config.api.url,
                pipe_id: this.config.api.pipe_id,
                request: requestBody,
                response: errorText
            });
            throw new Error(`API request failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.status || !data.data || !Array.isArray(data.data)) {
            return {};
        }

        const pricesMap = {};

        data.data.forEach(shipTo => {
            if (!shipTo.products || !Array.isArray(shipTo.products)) {
                return;
            }

            shipTo.products.forEach(product => {
                if (product.product_id && product.price !== undefined) {
                    pricesMap[product.product_id] = product.price;
                }
            });
        });

        return pricesMap;
    }

    formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(cents, theme.moneyFormat);
        }

        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
    }

    createSpinner() {
        return `
        <div class="loading-spinner" style="display: inline-flex; align-items: center; justify-content: center;">
            <svg class="spinner" viewBox="0 0 50 50" style="width: 22px; height: 22px;">
                <circle
                    class="path"
                    cx="25" cy="25" r="20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="5"
                    stroke-linecap="round"
                    stroke-dasharray="1, 150"
                    stroke-dashoffset="0"
                >
                    <animate
                        attributeName="stroke-dasharray"
                        values="1,150;90,60;90,60"
                        dur="1.5s"
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="stroke-dashoffset"
                        values="0;-35;-124"
                        dur="1.5s"
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
        </div>
    `;
    }
}

window.DynamicPricingCore = DynamicPricingCore;